import { FC } from "react";
import { BreadcrumbSchema, Seo, SITE_URL } from "@shared/components";
import { ORGANIZATION_SCHEMA, PERSON_SCHEMA, PRICING_FACTS } from "@shared/config/seo-schemas";

/**
 * Кто делает сервис. Закрывает две дыры сразу: E-E-A-T (у поисковиков и AI-систем
 * не было автора и происхождения продукта) и развязку с Polotno SDK — по запросу
 * «polotno» в выдаче стоит чужой canvas-редактор.
 *
 * Отсюда же берёт ссылку автор статей в блоге, поэтому страница обязана существовать
 * раньше, чем Person-разметка в BlogPostPage.
 */

const ABOUT_PAGE_SCHEMA: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${SITE_URL}/about#webpage`,
  url: `${SITE_URL}/about`,
  name: "О сервисе Полотно",
  inLanguage: "ru-RU",
  about: { "@id": `${SITE_URL}/#organization` },
  mainEntity: { "@id": `${SITE_URL}/about#person` }
};

export const AboutPage: FC = () => {
  return (
    <>
      <Seo
        title="О сервисе: кто делает Полотно | Полотно"
        description="Полотно — сервис расшифровки аудио и видео в текст. Как появился сервис, кто его делает, чего он не умеет и почему это не Polotno SDK с polotno.com."
        path="/about"
        jsonLd={[ORGANIZATION_SCHEMA, PERSON_SCHEMA, ABOUT_PAGE_SCHEMA]}
      />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: "О сервисе" }]} />

      <div className="container mx-auto px-4 py-12 md:px-6 lg:px-8">
        <h1 className="mb-4 text-center text-4xl font-bold">
          <span className="text-primary font-rublev">О</span> сервисе
        </h1>
        <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
          Полотно расшифровывает аудио и видео в текст. Ниже — как появился сервис, кто его делает и чего он не умеет.
        </p>

        <div className="mx-auto max-w-3xl space-y-10">
          <section>
            <h2 className="mb-4 text-2xl font-semibold">Как всё началось</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Сервис вырос из личной задачи: разобрать записи собственных собеседований и понять, что в них шло не
                так. Готовые инструменты либо не понимали русскую речь, либо требовали зарубежную карту, либо не умели
                брать запись по ссылке.
              </p>
              <p>
                Оказалось, что задача шире. Люди приносят созвоны, лекции, интервью, ролики с YouTube и VK — и всем
                нужно одно и то же: получить читаемый текст, а не поток слов без знаков препинания.
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Кто делает</h2>
            <p className="text-muted-foreground">
              За сервисом нет редакции и отдела продаж. Есть один разработчик, который сам пользуется расшифровками и
              сам отвечает в Telegram.
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Чего сервис не делает</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Честнее сказать сразу, чем разочаровать после оплаты. Бесплатного тарифа нет: каждая минута
                распознавания оплачивается поставщику модели. Начать можно с пробного периода —{" "}
                {PRICING_FACTS.trial.minutes} минут за {PRICING_FACTS.trial.price} ₽ на {PRICING_FACTS.trial.days} дней.
              </p>
              <p>
                Закрытые записи по ссылке сервис забрать не может: облачную запись Zoom и приватные видео нужно скачать
                и загрузить файлом. Диаризация плавает, когда несколько человек говорят в один микрофон в переговорке.
                Точность {PRICING_FACTS.accuracy} — это про записи хорошего качества, на шумном диктофоне будет хуже.
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Чем мы не являемся</h2>
            <p className="text-muted-foreground">
              Полотно — это <strong className="text-foreground">polotno.tech</strong>, расшифровка аудио и видео в
              текст. Есть похожий по имени продукт Polotno SDK на polotno.com — библиотека canvas-редактора для дизайна.
              Мы с ними никак не связаны: разные компании, разные продукты, общее только слово в названии.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};
